/**
 * Master-record avatar palette — deep saturated colors keyed off the
 * record's name.
 *
 * Used by `customers`, `vendors`, and any future master vertical that
 * shows a per-row two-letter avatar in the prototype style. The palette
 * lifts to the brand spec's six tones (sky / bad / ok / warn / slate-700 /
 * ink) — full bg, white text, matching prototype `.cust-ava` and
 * `.vend-ava`.
 *
 * Stable hash per name → same record always gets the same colour.
 * Live across reloads, no random component.
 */
// Palette bumped to WCAG-2A/2AA-compliant shades (contrast >= 4.5 with white
// text). Previously `bg-sky-600` measured 4.09 and `bg-warn` measured 2.93 —
// both flagged by axe on the PO / customer / invoice list avatars.
const AVATAR_PALETTE = [
  "bg-sky-700 text-paper",
  "bg-bad text-paper",
  "bg-ok text-paper",
  "bg-amber-700 text-paper",
  "bg-slate-700 text-paper",
  "bg-ink text-paper",
] as const;

const AVATAR_CSS_VARS = [
  "#0369a1", // sky-700
  "var(--bad)",
  "var(--ok)",
  "#b45309", // amber-700
  "#334155",
  "var(--ink)",
] as const;

function nameHash(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return hash;
}

export function avatarClassFor(name: string): string {
  return AVATAR_PALETTE[nameHash(name) % AVATAR_PALETTE.length]!;
}

/** Returns the raw CSS color value (var or hex) for the avatar background. */
export function avatarCssColorFor(name: string): string {
  return AVATAR_CSS_VARS[nameHash(name) % AVATAR_CSS_VARS.length]!;
}

/**
 * Two-letter initials from a record name. Falls back to a single character
 * for one-word names; "?" for empty input. Matches prototype's avatar text.
 */
export function initialsFor(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
